import { createContext, useContext, useMemo } from 'react';
import { useCollectionState } from '@sail/react-aria';
import { FauxTreeItem } from '../components/FauxTreeItem';
const FauxTreeProviderContext = createContext(null);
export const FauxTreeProvider = FauxTreeProviderContext.Provider;
export function useDomItems() {
    return useContext(FauxTreeProviderContext) || [];
}
function getDataItems(dataCollection) {
    const groupedItems = {};
    let previousItems = new Set();
    const allItems = new Set();
    const allNodes = [
        ...dataCollection
    ];
    allNodes.forEach((currentItem)=>{
        if (currentItem) {
            if (currentItem.type === 'section') {
                if (typeof currentItem.childNodes !== 'undefined') {
                    var _currentItem_childNodes, _map;
                    const childItems = (_map = [
                        ...(_currentItem_childNodes = currentItem.childNodes) !== null && _currentItem_childNodes !== void 0 ? _currentItem_childNodes : []
                    ].map((item)=>item.key)) !== null && _map !== void 0 ? _map : [];
                    childItems.forEach((childItem)=>allItems.add(childItem));
                    groupedItems[currentItem.key] = new Set(childItems);
                } else {
                    // Note: if the section does not have childNodes, it's
                    // because the previous ones are the children.
                    groupedItems[currentItem.key] = new Set(previousItems);
                    previousItems = new Set();
                }
            } else {
                previousItems.add(currentItem.key);
                allItems.add(currentItem.key);
            }
        }
    });
    return {
        groupedDataItems: groupedItems,
        allDataItems: [
            ...allItems
        ]
    };
}
export function getFullDataItems(dataCollection) {
    const allItems = new Map();
    const allNodes = [
        ...dataCollection
    ];
    allNodes.forEach((currentItem)=>{
        if (currentItem) {
            if (currentItem.type === 'section') {
                if (typeof currentItem.childNodes !== 'undefined') {
                    var _currentItem_childNodes;
                    const childItems = [
                        ...(_currentItem_childNodes = currentItem.childNodes) !== null && _currentItem_childNodes !== void 0 ? _currentItem_childNodes : []
                    ];
                    childItems.forEach((childItem)=>{
                        allItems.set(childItem.key, childItem);
                    });
                }
            } else {
                allItems.set(currentItem.key, currentItem);
            }
        }
    });
    return allItems;
}
function getDomItems(domItems) {
    // Note: If the FauxTree sets DOM elements to render items, use them to select
    // all the elements
    const groupedDomItems = {};
    const allDomItems = [];
    if (Array.isArray(domItems)) {
        domItems.forEach((element)=>{
            if ((element === null || element === void 0 ? void 0 : element.type) === FauxTreeItem) {
                allDomItems.push(element.props.id);
            } else {
                const groupId = element.props.id;
                if (Array.isArray(element.props.children)) {
                    const childItems = element.props.children.flatMap((child)=>{
                        var _child_props;
                        var _child_props_id;
                        return (_child_props_id = (_child_props = child.props) === null || _child_props === void 0 ? void 0 : _child_props.id) !== null && _child_props_id !== void 0 ? _child_props_id : [];
                    });
                    groupedDomItems[groupId] = new Set(childItems);
                    allDomItems.push(...childItems);
                }
            }
        });
    }
    return {
        groupedDomItems,
        allDomItems
    };
}
function getGroupedItems(dataItems, domItems, selectedItems, disabledItems) {
    // Step 1: Calculate all the data items and group them.
    let { groupedDataItems: groupedItems, allDataItems: allItems } = dataItems;
    // Step 2: If there are elements in DOM, use them instead of
    // the data collection.
    if (domItems.allDomItems.length) {
        groupedItems = domItems.groupedDomItems;
        allItems = domItems.allDomItems;
    }
    // Step 3: Calculate selected items that are visible (if the data is filtered, i.e. faux tree in a search field).
    const selectedFilteredItems = [
        ...selectedItems
    ].filter((item)=>allItems.some((dataItem)=>dataItem === item));
    const selectedHiddenItems = [
        ...selectedItems
    ].filter((item)=>!selectedFilteredItems.some((dataItem)=>dataItem === item));
    const allItemsAreDisabled = allItems.every((item)=>disabledItems.has(item));
    // Step 4: Calculate all the info needed for select all item.
    const selectAllItem = {
        selectedCount: selectedFilteredItems.length,
        totalCount: allItems.length,
        selectedHiddenCount: selectedHiddenItems.length,
        checkableKeys: allItems.filter((item)=>!(disabledItems.has(item) && !selectedItems.has(item))).concat(selectedHiddenItems),
        uncheckableKeys: [
            ...disabledItems
        ].filter((item)=>selectedItems.has(item)).concat(selectedHiddenItems),
        isDisabled: allItemsAreDisabled
    };
    // Step 5: Calculate all the info needed for select group items.
    const selectGroupItems = new Map();
    const groups = Object.keys(groupedItems);
    for(let i = 0; i < groups.length; i++){
        const group = groups[i];
        const childItems = [
            ...groupedItems[group]
        ];
        // Note: we need to keep the previous state of the rest of the groups
        // when we are checking or unchecking the "select group"
        const selectedItemsOutsideGroup = [
            ...selectedItems
        ].filter((item)=>!groupedItems[group].has(item));
        const allGroupItemsAreDisabled = childItems.every((item)=>disabledItems.has(item));
        selectGroupItems.set(group, {
            selectedCount: childItems.filter((item)=>selectedItems.has(item)).length,
            totalCount: childItems.length,
            selectedForeignCount: selectedItemsOutsideGroup.length,
            checkableKeys: childItems.filter((item)=>!(disabledItems.has(item) && !selectedItems.has(item)))// Note: we have to preserved the rest of unchecked checkboxes outside of this group as unchecked
            .concat(selectedItemsOutsideGroup),
            isDisabled: allGroupItemsAreDisabled,
            uncheckableKeys: childItems.filter((item)=>disabledItems.has(item) && selectedItems.has(item))// Note: we have to preserve the checked checkboxes outside of this group as checked
            .concat(selectedItemsOutsideGroup)
        });
    }
    return {
        selectGroupItems,
        selectAllItem
    };
}
export default function useFauxTreeState() {
    const state = useCollectionState();
    const treeCollection = state.collection;
    const originalDomItems = useDomItems();
    const dataItems = useMemo(()=>getDataItems(treeCollection), [
        treeCollection
    ]);
    const domItems = useMemo(()=>getDomItems(originalDomItems), [
        originalDomItems
    ]);
    const selectedItems = state.selectionManager.selectedKeys;
    const disabledItems = state.selectionManager.disabledKeys;
    const { selectGroupItems, selectAllItem } = useMemo(()=>getGroupedItems(dataItems, domItems, selectedItems, disabledItems), [
        dataItems,
        disabledItems,
        domItems,
        selectedItems
    ]);
    const isMultipleSelection = state.selectionManager.selectionMode === 'multiple';
    return {
        isMultipleSelection,
        selectGroupItems,
        selectAllItem
    };
}

//# sourceMappingURL=useFauxTreeState.js.map